import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { SectionsBar } from '@sorare/core/src/atoms/ui/SectionsBar';
import type { Color } from '@sorare/core/src/style/types';

import type { StreakProgressBar_so5Lineup } from './__generated__/index.graphql';

const StyledSBHorizontal = styled(SBHorizontal)`
  width: 100%;
  align-self: flex-end;
`;

export const StreakProgressBar = ({
  currentThresholdScore,
  lineup,
}: {
  currentThresholdScore: number;
  lineup: StreakProgressBar_so5Lineup;
}) => {
  const { so5Rankings } = lineup;

  const target = currentThresholdScore;

  const maxScoreDisplayed = currentThresholdScore * 1.33;

  const currentScore = so5Rankings.at(0)?.score || 0;
  const sections = [
    { width: `${(target / maxScoreDisplayed) * 100}%` },
    {
      width: `${(1 - target / maxScoreDisplayed) * 100}%`,
      background:
        currentScore >= target
          ? ('linear-gradient(292deg, #FF4E22 14.11%, #FEF314 85.81%)' as Color)
          : ('linear-gradient(292deg, rgba(255, 78, 34, 0.16) 14.11%, rgba(254, 243, 20, 0.16) 85.81%)' as Color),
      content: (
        <StyledSBHorizontal>
          <BrandXXS
            as="p"
            color={
              currentScore >= target ? 'var(--c-nl-400)' : 'var(--c-nd-400)'
            }
          >
            {target}
          </BrandXXS>
        </StyledSBHorizontal>
      ),
    },
  ];

  return (
    <SectionsBar
      sections={sections}
      cursorProps={{
        color: 'var(--c-white)',
        rightPosition: `${Math.min(Math.max(100 - (currentScore / maxScoreDisplayed) * 100, 0), 100)}%`,
      }}
    />
  );
};

StreakProgressBar.fragments = {
  so5Lineup: gql`
    fragment StreakProgressBar_so5Lineup on So5Lineup {
      id
      so5Rankings {
        id
        score
      }
    }
  ` as TypedDocumentNode<StreakProgressBar_so5Lineup>,
};
